import type { OnboardingStateMap } from '~/onboarding/types'
import { defaultUserPreferences } from '~/utils/userPreferences/defaults'
import type {
  KeyboardShortcut,
  UserOnboardingPreferences,
  UserPreferencesPayload,
  UserShortcuts,
} from '~/utils/userPreferences/types'

function safeParseJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return null
  }
}

export function parseUserPreferencesJson(text: string): unknown {
  return safeParseJson(text)
}

function isKeyboardShortcut(value: unknown): value is KeyboardShortcut {
  if (!value || typeof value !== 'object') {
    return false
  }
  const shortcut = value as Partial<KeyboardShortcut>
  return (
    typeof shortcut.key === 'string' &&
    typeof shortcut.meta === 'boolean' &&
    typeof shortcut.ctrl === 'boolean' &&
    typeof shortcut.shift === 'boolean' &&
    typeof shortcut.alt === 'boolean'
  )
}

function isOnboardingStateMap(value: unknown): value is OnboardingStateMap {
  if (typeof value !== 'object' || value === null) {
    return false
  }
  for (const progress of Object.values(value as Record<string, unknown>)) {
    if (typeof progress !== 'object' || progress === null) {
      return false
    }
    const typedProgress = progress as Record<string, unknown>
    if (
      typeof typedProgress.tourId !== 'string' ||
      typeof typedProgress.version !== 'number' ||
      typeof typedProgress.currentStep !== 'number' ||
      typeof typedProgress.updatedAt !== 'string'
    ) {
      return false
    }
    if (
      typedProgress.status !== 'not_started' &&
      typedProgress.status !== 'in_progress' &&
      typedProgress.status !== 'completed' &&
      typedProgress.status !== 'dismissed'
    ) {
      return false
    }
  }
  return true
}

function normalizeUserOnboardingPreferences(
  value: unknown,
): UserOnboardingPreferences | undefined {
  if (typeof value !== 'object' || value === null) {
    return undefined
  }
  const onboarding = value as Partial<UserOnboardingPreferences>
  if (onboarding.version !== 1 || typeof onboarding.updatedAt !== 'number') {
    return undefined
  }
  if (!isOnboardingStateMap(onboarding.state)) {
    return undefined
  }
  return {
    version: 1,
    updatedAt: onboarding.updatedAt,
    state: onboarding.state,
  }
}

/**
 * Fusionne un payload partiel (ex. anciennes données sans `editModeToggle`) avec les valeurs par défaut.
 */
export function normalizeUserPreferencesPayload(value: unknown): UserPreferencesPayload | null {
  const defaults = defaultUserPreferences()
  if (!value || typeof value !== 'object') {
    return null
  }
  const payload = value as Partial<UserPreferencesPayload>
  if (payload.version !== 1) {
    return null
  }
  if (typeof payload.updatedAt !== 'number') {
    return null
  }
  if (
    payload.colorModePreference !== 'light' &&
    payload.colorModePreference !== 'dark' &&
    payload.colorModePreference !== 'system'
  ) {
    return null
  }
  const shortcuts = payload.shortcuts as Partial<UserShortcuts> | undefined
  if (!shortcuts || typeof shortcuts !== 'object') {
    return null
  }
  if (!isKeyboardShortcut(shortcuts.toolboxToggle) || !isKeyboardShortcut(shortcuts.geolocate)) {
    return null
  }
  const editModeToggle = isKeyboardShortcut(shortcuts.editModeToggle)
    ? shortcuts.editModeToggle
    : defaults.shortcuts.editModeToggle
  return {
    version: 1,
    updatedAt: payload.updatedAt,
    colorModePreference: payload.colorModePreference,
    welcomeSplashDone: payload.welcomeSplashDone === true,
    shortcuts: {
      toolboxToggle: shortcuts.toolboxToggle,
      geolocate: shortcuts.geolocate,
      editModeToggle,
    },
    onboarding: normalizeUserOnboardingPreferences(payload.onboarding),
  }
}
